import { useEffect } from "react";
import { createFileRoute, redirect, useParams, useRouter } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { CourseStudy } from "@/pages/Courses/CourseStudy";
import { LoaderSpinner } from "@/components/ui/LoaderSpinner";
import { kursyAll } from "@/kursy/general";
import { getSubscriptionCourseOptions } from "@/api/queryOptions";
import { queryClient } from "@/main";
import type { CourseKey } from "@/api/api.types";

const CourseProductView = (): JSX.Element => {
	const { courseKey, productId } = useParams({
		from: "/(app)/_app/course/$courseKey/$productId",
	});
	const router = useRouter();
	const { data: subscription } = useSuspenseQuery(
		getSubscriptionCourseOptions(courseKey as CourseKey)
	);
	const products = subscription?.products ?? [];
	const product = products.find((p) => p.id === Number(productId));
	const parentKurs = kursyAll.find((k) => k.slug === courseKey);

	useEffect(() => {
		if (!product) {
			void router.navigate({
				to: "/course/$courseKey",
				params: { courseKey },
				replace: true,
			});
		}
	}, [product, router, courseKey]);

	if (!product) {
		return (
			<div className="flex justify-center items-center h-screen">
				<LoaderSpinner className="p-3" size={6} />
			</div>
		);
	}
	return <CourseStudy product={product} kurs={parentKurs} />;
};

export const Route = createFileRoute("/(app)/_app/course/$courseKey/$productId")({
	component: CourseProductView,
	beforeLoad: async ({ params }) => {
		const data = await queryClient.ensureQueryData(
			getSubscriptionCourseOptions(params.courseKey as CourseKey)
		);
		// Parent layout owns the !has_access and empty-products UX; bailing out
		// here prevents a dispatcher bounce loop when the API fails or returns no products.
		if (!data || !data.has_access || data.products.length === 0) return;
		const targetId = Number(params.productId);
		if (Number.isNaN(targetId) || !data.products.some((p) => p.id === targetId)) {
			redirect({
				to: "/course/$courseKey",
				params: { courseKey: params.courseKey },
				throw: true,
			});
		}
	},
});
